import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateMinimalist({ user, projects, language = 'id' }) {
  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white p-4 sm:p-6 md:p-8 max-w-[21cm] mx-auto shadow-sm print:p-8 print:shadow-none print:max-w-full" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header Minimalist */}
      <div className="cv-section border-b-2 border-gray-900 pb-4 sm:pb-6 mb-4 sm:mb-6 avoid-break">
        <h1 className="text-2xl sm:text-3xl md:text-2xl font-light tracking-tight text-gray-900 mb-1 text-left">
          {getFormattedUserName(user) || 'Your Name'}
        </h1>
        <p className="text-base sm:text-lg text-gray-600 font-light">{user?.job || 'Professional Title'}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
        {/* Left Column - Contact */}
        <div className="col-span-1 space-y-4 sm:space-y-6">
          {/* Contact */}
          <div className="cv-section avoid-break">
            <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
              {t('contact', language)}
            </h2>
            <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-600">
              {user?.email && <p className="break-words">{user.email}</p>}
              {user?.phone && <p>{user.phone}</p>}
              {user?.location && <p>{user.location}</p>}
            </div>
          </div>

          {/* Skills */}
          {user?.skills && user.skills.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('skills', language)}
              </h2>
              <div className="space-y-1">
                {user.skills.map((skill, i) => (
                  <div key={i} className="text-xs sm:text-sm text-gray-700">• {skill}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Main Content */}
        <div className="col-span-1 sm:col-span-2 space-y-4 sm:space-y-6">
          {/* Summary */}
          {user?.summary && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('summary', language)}
              </h2>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed text-justify">{user.summary}</p>
            </div>
          )}

          {/* Experience */}
          {user?.experience && user.experience.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('workExperience', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {user.experience.map((exp, i) => (
                  <div key={i} className="cv-experience-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{exp.position}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{exp.company} • {exp.period}</p>
                    {exp.description && (
                      <p className="text-xs sm:text-sm text-gray-700 mt-1 leading-relaxed text-justify">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education - Higher Education */}
          {higherEducation.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('higherEducation', language)}
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {higherEducation.map((edu, i) => (
                  <div key={i} className="cv-education-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{edu.school || edu.institution} • {edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education - K-12 */}
          {k12Education.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('formalEducation', language)}
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {k12Education.map((edu, i) => (
                  <div key={i} className="cv-education-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{edu.school || edu.institution} • {edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects */}
          {projects && projects.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-xs sm:text-sm font-semibold uppercase tracking-wider text-gray-900 mb-2 sm:mb-3 border-b border-gray-300 pb-1">
                {t('projects', language)}
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {projects.map((proj, i) => (
                  <div key={i} className="cv-project-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{proj.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-700 text-justify">{proj.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
